/**
 * The demo entrypoint — one real case, run end to end, from the browser.
 *
 * `app/demo.py` is mounted into `app.main` (E-40, Task 12) — the one process
 * the server runs, same origin as `/api/v1`. Its own base URL and its own
 * client here (rather than the generated contract client) survive that
 * mount unchanged: the six `/demo/...` paths stay theirs, only the origin
 * they answer on moved from a fourth standalone process to this one.
 *
 * Nothing in this file decides anything. Splitting, brackets, closure and the
 * grating width are backend results; this transports them.
 */

import type { DemoFinding, DemoOutline, LengthSource } from "@/lib/outline";
import type { CutoutShape, ReferenceKind, ReferenceSide } from "@/lib/placement";

import type { ClosureCheck, ExtractedValue, PlanEdge, PlanNode, SourceRef } from "./preview/types";

// Re-exported so a caller reading `DemoRunResult`'s `outlines` and
// `outlineFindings` fields finds their element types here too, without a
// second import from `@/lib/outline`.
export type { DemoFinding, DemoOutline };

/**
 * Where the demo routes answer. Same origin by default (Task 12): `/demo/*`
 * runs through Vite's dev proxy and Caddy's `/demo/*` block exactly like
 * `/api/*` does — a real base URL is only for the rare case of the
 * standalone `uvicorn app.demo:app` process, and it names the origin, not
 * a path prefix.
 */
const DEMO_BASE_URL: string = import.meta.env.VITE_DEMO_URL ?? "";

/**
 * The four steps the screen names while a run is in flight.
 *
 * They are the pipeline of `demo.py`, in order. `extract` covers OCR and the
 * semantic call together — the backend measures them as one block, so the two
 * screen steps that sit inside it share that one measurement.
 */
export type DemoStep = "prepare" | "orient" | "extract" | "plan";

export interface DemoSourceRef extends SourceRef {
  /** True when the location came out of the OCR stage rather than the model. */
  fromOcr: boolean;
  /**
   * True when the box is the model's own claim and stage ② did not back it.
   *
   * The screen offers no "click the value, see the spot" for those. An invented
   * rectangle over a drawing is worse than an honest absence — on hand
   * sketches this is the common case, not the exception (pre-trial finding 5).
   */
  bboxUnverified: boolean;
}

export interface DemoValue extends Omit<ExtractedValue, "source"> {
  /** Verbatim reading, also at the top level so it sits next to the value. */
  originalText: string;
  source: DemoSourceRef | null;
  /** Rules from the schema module that concern this reading, e.g. `segment_kind_not_a_length`. */
  issues: string[];
}

export interface DemoRunTotals {
  elements: number;
  /**
   * Brackets for the run, counted once per joint — **not** the sum of the
   * sections' `consoles`. Neighbouring sections share the bracket between them.
   */
  consoles: number;
  /**
   * False where the figure is an upper bound rather than a count: two Teilzüge
   * meeting at a corner share the bracket there, and the extraction does not
   * say whether they meet. Where this is false the screen says "höchstens" and
   * prints `consolesNote` — a bracket count read as exact is a figure somebody
   * would order against.
   */
  consolesExact: boolean;
  /** Why it is an upper bound, in German, from the backend. Empty when exact. */
  consolesNote: string;
  /**
   * The count with what it is made of, in German — „12 Konsolen — 3 Läufe, je
   * n+1 · 1 geteilte Eckkonsole · …" (design §4.2.1).
   *
   * Printed **instead of** the bare figure on the planning screen. The word
   * „höchstens" used to do a job beyond precision: it was a reason to look.
   * Dropping it while the bracket *type* (193/222/272), the height and the
   * mitre are still outside the model would make the number not safer but
   * only more self-assured — so what replaces it is the derivation.
   *
   * Empty where nothing was laid.
   */
  consolesDerivation?: string;
  /** Continuous pieces of grating. More than one means something interrupts the run. */
  stretches: number;
  lengthMm: number;
}

export interface DemoPlanEdge extends PlanEdge {
  /**
   * What the drawing stated: `yes`, `no` or `unknown` — a different claim from
   * `hasGrating`, which is what the parts list did with it. A section the model
   * hedged on is planned as goods; showing it as settled would hide the one
   * thing a person is needed for (A-014).
   */
  gratingPresence: "yes" | "no" | "unknown";
}

/** One laid element of a run — planning path only (design §4.1). */
export interface DemoPlacedElement {
  placementId: string;
  wallIndex: number;
  offsetMm: number;
  lengthMm: number;
  /** Resolved, never null — the element's own type or the job's. */
  gratingType: string;
  /** How wide it is actually built: a special width, or the type's own. */
  widthMm: number;
  label: string | null;
  /** How it is cut at either end — `gerade`, or a mitre leaning one way or
   * the other. Two mitre values because the drawing has to know which way the
   * cut leans and may not work it out for itself. */
  cutStart?: "gerade" | "gehrung_aussen" | "gehrung_innen" | "schraeg";
  cutEnd?: "gerade" | "gehrung_aussen" | "gehrung_innen" | "schraeg";
  /** How far the element reaches past either end, into the corner square.
   * Beside the cut, not folded into it: a filled corner is cut straight and
   * grows all the same. */
  growsAtStartMm?: number;
  growsAtEndMm?: number;
  /** What the workshop cuts. Resolved, never null: the wall stretch where
   * nothing grew it. */
  blankLengthMm?: number;
  /** The derived blank a typed one displaced, `null` where nobody typed. */
  displacedBlankMm?: number | null;
  /** What the derivation said when that blank was typed. */
  blankSnapshotMm?: number | null;
  /** The holes drilled into this element, in wall order — empty or absent
   * where there is none. */
  cutouts?: DemoElementCutout[];
}

/** One hole on one element, as the answer states it (backend `ElementCutoutOut`). */
export interface DemoElementCutout {
  id: string;
  shape: CutoutShape;
  diameterMm: number | null;
  sizeAlongMm: number | null;
  sizeAcrossMm: number | null;
  /** The workshop's zero — a named conversion over `growsAtStartMm` (§11). */
  fromBlankStartMm: number;
  acrossMm: number;
  obstacleType: string;
  confirmed: boolean;
  confirmedOn: string | null;
  referenceKind: ReferenceKind;
  /** „F2 (Fenstertür, dahinter)" — `null` at the wall start. */
  referenceLabel: string | null;
  fromReferenceMm: number | null;
  cutLengthMm: number;
  surchargeNet: string | null;
  surchargeTyped: boolean;
  displacedNet: string | null;
}

export interface DemoPartialRun {
  id: string;
  label: string;
  topologyConfidence: "high" | "medium" | "low";
  nodes: PlanNode[];
  edges: DemoPlanEdge[];
  closure: ClosureCheck;
  totals: DemoRunTotals;

  /**
   * The laid elements, in walking order — planning path only, empty elsewhere.
   *
   * The board draws its consoles off these: joint `i` sits between element `i`
   * and element `i+1`. It projects them; **which** joints exist and what they
   * mean is stated by the backend, in `corners` and `bridged`.
   */
  elements?: DemoPlacedElement[];
  /** Joints sitting on a wall boundary — the geteilte Eckkonsole (§4.1). */
  corners?: number[];
  /**
   * What each of those corners is — `"aussen"`, `"innen"`, or `""` where the
   * walk does not turn there. Paired with `corners` by position.
   *
   * The outline's own shape and never anybody's mark, derived once on the
   * server (`schema.corner_kinds`). The screen needs it before a corner
   * carries anything: filling an outer corner grows the named grating by its
   * width, filling an inner one takes the same square off the other
   * (E-47/§12).
   */
  cornerKinds?: string[];
  /** Joints reaching across an interruption the run walks over (§3.7). */
  bridged?: number[];
  /**
   * Which **elements** — not joints — carry a bracket under their own
   * middle: any element over 1.000 mm. Indexes into `elements`, the same
   * array `corners` and `bridged` index into `elements.length - 1` joints —
   * two numberings, never converted into one another.
   */
  middles?: number[];
  /**
   * Whether the run comes back to where it started. Stated, not guessed: a
   * ring has no ends and therefore `n` brackets instead of `n+1`, and from
   * `corners` alone it is indistinguishable from an open run around three
   * corners.
   */
  ring?: boolean;
}

export interface DemoPartsSection {
  runId: string;
  label: string;
  stretch: number;
  lengthMm: number;
  elements: number[];
  /**
   * What each element above is actually cut to, in the same order.
   *
   * Empty where nothing is mitred — the same statement as „every blank is its
   * wall stretch", and the parts list then shows one column as it always has.
   */
  blanks?: number[];
  /**
   * Brackets **if this section stood on its own**. Never summed on screen —
   * the truthful figure per run is `DemoPartialRun.totals.consoles`.
   */
  consoles: number;
  /** The section lengths this one was merged from, where it was merged at all. */
  mergedFrom: number[];
}

export interface DemoPageOrientation {
  page: number;
  appliedDegrees: number;
  outcome: string;
  certain: boolean;
  reason: string;
}

export interface DemoOrientation {
  /** Null when the sheets of one document were turned differently. */
  appliedDegrees: number | null;
  outcome: string;
  certain: boolean;
  reason: string;
  pages: DemoPageOrientation[];
}

export interface DemoDocument {
  id: string;
  filename: string;
  mimeType: string;
  pageCount: number;
  sizeBytes: number;
  renderDpi: number;
}

export interface DemoGrating {
  type: string | null;
  label: string | null;
  widthMm: number | null;
  /** `request` — stated by the customer · `extraction` — read off the drawing · `none`. */
  source: "request" | "extraction" | "none";
  note: string;
}

/** One grating type, with the band its elements have to stay inside. */
export interface GratingSpecOut {
  type: string;
  widthMm: number;
  minLengthMm: number;
  maxLengthMm: number;
  standardLengthMm: number;
}

/**
 * One row of the bracket part of the parts list — planning path only, the
 * wire twin of the backend's `ConsoleLineOut` (`apps/api/app/demo.py`).
 *
 * Grouped by article on the backend already (`pricing/consoles.py:console_lines`)
 * — a real case carries 21 brackets, and twenty-one rows of one piece each is
 * a list that gets skipped. This browser renders one row per entry and never
 * explodes `count` back into single rows.
 */
export interface DemoConsoleLine {
  /** `null` is not an error row — a bracket that will be built and that the
   * catalogue has no article for. It keeps its count and loses its price. */
  articleNo: string | null;
  label: string;
  count: number;
  /** A decimal amount, as a string — see `formatMoney` in `@/lib/format` for
   * why this never becomes a JS number before it reaches the screen. */
  unitPriceNet: string | null;
  totalNet: string | null;
  /** How many of this row a person set by hand, ahead of the catalogue lookup. */
  overridden: number;
  /** German, for the screen. Empty where the row needs no explanation. */
  note: string;
}

/**
 * Where one bracket a run counted actually sits, and what article applies to
 * it — the wire twin of the backend's `ConsoleMarkOut` (`apps/api/app/demo.py`).
 *
 * Addressed by `key`, never by a position in this list — `planning.py`'s own
 * reasoning for `ConsolePosition.key`: move a neighbouring placement and an
 * index would point at a different bracket, and an override sent back later
 * would land on one nobody meant.
 */
export interface DemoConsoleMark {
  /** `"<placementId>:<spot>"` — the address an override travels against. */
  key: string;
  placementId: string;
  spot: "start" | "mitte" | "ende";
  widthMm: number;
  shared: boolean;
  corner: boolean;
  /** What actually applies: the article a person chose by hand where
   * `consoleOverrides` names this address, the proposal otherwise. */
  articleNo: string | null;
  /**
   * What the catalogue proposes for this width now, regardless of any
   * override — kept even where a person overruled it, so the screen can say
   * later that a hand-set article no longer matches what would be proposed.
   */
  proposedArticleNo: string | null;
  /**
   * The active articles that carry this bracket's width, in article-number
   * order — the server's own `fits` (`pricing.fitting_articles`), never
   * rebuilt here. The chooser sets every other catalogue article apart and
   * says why in words, but keeps it selectable (A-014; the calculation asks
   * again). `undefined` on an answer that predates the field — the chooser
   * then lists the catalogue as it always did.
   */
  fittingArticleNos?: string[];
}

/**
 * One article the bracket chooser may offer as an alternative — the wire twin
 * of the backend's `ConsoleCatalogueEntryOut` (`apps/api/app/demo.py`).
 *
 * Not the same list as `DemoConsoleLine`: that one names only the articles
 * the *current* plan actually uses, which on an ordinary job of one width, or
 * on a mark whose proposal is `null` (design §4.3's 308 mm), leaves nothing
 * to choose *between* — exactly the case a person most needs the chooser
 * for. This is the dozen rows the server already holds, active articles
 * only, so the menu always has something to offer (Critical 2, whole-branch
 * review 05.09.2026).
 */
export interface DemoConsoleCatalogueEntry {
  articleNo: string;
  label: string;
  family: string;
}

export interface DemoIssue {
  source: string;
  rule: string;
  severity: string;
  message: string;
  path: string;
  metric: string;
  valueIds: string[];
}

export interface DemoRunResult {
  caseId: string;
  title: string;
  provider: DemoProvider;
  /**
   * What a replayed record itself says produced the reading, e.g. `"mistral"`
   * or `"user_entered"` — the backend's word for one a person typed in by
   * hand, never sent to a model. `null` for a live run, and for a replay of a
   * record written before this field existed.
   *
   * Not `provider` above, which only ever names the transport this request
   * travelled over. The "erfasst von Hand" badge belongs on this field, never
   * on `provider` or on the case id — a case re-recorded from a live model
   * run must stop reading as hand-entered the moment its record does.
   */
  recordedProvider: string | null;
  promptId: string;
  ocrModel: string;
  semanticModel: string;

  /**
   * Which model read the *contour* — `"mistral"`, `"openai"`, or `null` when
   * no contour call happened (a replay, or an unusable document).
   *
   * Deliberately separate from `semanticModel`: since 14.08.2026 the two can
   * be different providers. The measurement behind that (36 workbench runs on
   * L2/L3/L4) found the contour stage to be the one place where the choice
   * changes the answer — on a photographed hand sketch one model reproduced
   * the drawing six times out of six while the other reported a closed ring
   * on an open sheet five times out of six. Showing one model name for both
   * stages would say something untrue about where the page travelled.
   */
  contourProvider: string | null;
  contourModel: string | null;

  /**
   * Why that provider and not the other, in one sentence from the backend.
   *
   * The second provider only ever sees abstracted sheets (`Stage0-Simplify`)
   * — the approval of 06.08.2026 names Mistral, and the customer conversation
   * about the second one is still running. This line is how a person reading
   * the run afterwards can tell which of the two applied without
   * reconstructing it from configuration.
   */
  contourBasis: string | null;
  /**
   * The day the replayed record was written, `YYYY-MM-DD`, from the record.
   * `null` for a live run — there is nothing to date but now.
   */
  recordedAt: string | null;
  documentClass: string;

  /**
   * Why the model classified the sheet the way it did — its own words, and
   * only ever set for a class other than `"valid"`.
   *
   * That is the case where the run ends with an empty screen: the prompt
   * forbids a rejected or special-case document from carrying measurements,
   * so none are read, no runs are formed and no contour is asked for. Without
   * this sentence the outcome looks exactly like a model that found nothing —
   * and a judgement to argue with is not a defect to chase.
   */
  classReason: string | null;
  usable: boolean;
  errorClass: string | null;
  document: DemoDocument;
  /**
   * The run's `OutlineDocument`, serialised (`document_to_json` on the
   * backend) — not `document` above, which is the sheet's own metadata
   * (filename, page count) and never the editable shape.
   *
   * This is what Task 14's two entry paths send on as version 1's own
   * `document` (`POST /cases/{id}/versions`, design §4.1 and §9) — the one
   * field that lets this client persist a run without inventing its own
   * serialiser. `null` wherever no editable document exists yet (the
   * enumeration-only fallback, or a run whose document `_contour_document`
   * could not build).
   *
   * Optional, unlike every other field on this interface: every existing
   * fixture in this test suite builds `DemoRunResult` by hand, field for
   * field, and a new *required* key would ask every one of them to grow a
   * line for a field their own test does not exercise.
   */
  documentJson?: Record<string, unknown> | null;
  /**
   * What was laid on the shape, as the stored version carries it — the
   * `plan_version` row's own `planning_state`, handed straight back
   * (backend `DemoRunOut.planning_state`).
   *
   * `documentJson` beside it brings back the geometry; this brings back the
   * plan. Without it, reopening a Vorgang showed the walls and the parts
   * list of a board with nothing on it, because nothing on the wire said
   * where a person had put a piece.
   *
   * `null` off the replay path: the stateless `/demo/plan/*` endpoints
   * answer a state the client itself just sent and still holds, and a screen
   * hydrating from that would overwrite what it had already drawn.
   *
   * Optional for the reason `documentJson` above is — the fixtures in this
   * suite build `DemoRunResult` by hand.
   */
  planningState?: DemoPlanningState | null;
  /**
   * Whether the outline is a person's drawing, not a reading — what the
   * schema screen says „von Hand gezeichnet" by. On a stored Vorgang it is
   * Stand 1's own origin (`plan_version.trigger`), never the page count:
   * since 13.10.2026 uploads count their pages, and a Vorgang with a sheet
   * can still carry an outline drawn by hand. Always sent by the backend.
   */
  drawnByHand: boolean;
  orientation: DemoOrientation;
  grating: DemoGrating;
  values: DemoValue[];
  runs: DemoPartialRun[];

  /**
   * Every arrangement the runs could form, the unconnected one always last.
   * `chosen` marks the one this response was planned from — the requested
   * id, an id the backend drew on its own (`autoOutline`, `chosenBy:
   * "system"`), or the unconnected arrangement where neither applies. The
   * backend may draw a shape nobody clicked; it is never shown as CONFIRMED
   * without a person doing that themselves (A-014) — `chosenBy` says who
   * picked it, confirming is a separate, later act that lives only in this
   * browser tab.
   */
  outlines: DemoOutline[];

  /**
   * The arrangement the backend considers unambiguous, or `null` where two
   * readings compete — the wire twin of `schema.rank_candidates`' own
   * `auto`. Reported whether or not a request actually used it: even a
   * request with an explicit `outline` gets this back, so the screen can
   * offer the automatic reading at any time (backend `DemoRunOut.auto_outline`).
   */
  autoOutline: string | null;

  /**
   * Why `autoOutline` is what it is: `"single_candidate"` — there was
   * nothing else to offer —, `"dominant"`, or `"label_agreement"`, where the
   * sides the reading named fit together in only one of the equally close
   * arrangements. `null` exactly when `autoOutline` is; the escalation that
   * then applies travels as an `"ambiguous_shape"` entry in `issues` instead,
   * not through this field. The backend's own `AutoReason` vocabulary, passed
   * through verbatim.
   */
  autoReason: string | null;

  /**
   * Who this response's drawn shape came from: `"system"` for
   * `autoOutline`, `"user"` for an explicit `outline` in the request,
   * `null` where neither applies and the unconnected arrangement stands.
   *
   * `"system"` is a proposal, not a decision, and has to read as one on
   * screen — A-014 rests on this field once shapes start arriving unasked.
   * It is never `"confirmed"`: that step happens only in this browser tab,
   * only on a person's own click, and nothing on the wire can say it
   * already happened.
   */
  chosenBy: "system" | "user" | null;

  /**
   * Whether the chosen shape still says what the drawing said. Empty for the
   * unconnected arrangement — there is nothing to reconcile a shape against
   * when no shape was claimed.
   */
  outlineFindings: DemoFinding[];

  /** Every wall a correction touched, in segment order. Empty where nobody corrected anything. */
  adjustments: DemoAdjustment[];

  /**
   * Segment indices whose correction was reined in to keep a neighbouring
   * wall at or above 1 mm. Reported rather than refused: the drag did
   * something, just not all of what was asked.
   */
  editsClamped: number[];

  partsList: DemoPartsSection[];
  totals: DemoRunTotals;
  issues: DemoIssue[];

  /**
   * The grating catalogue, as the backend holds it (design §3.2).
   *
   * It travels so the board can show a remainder falling below the smallest
   * element **while the pointer is still moving**, without 400 becoming a
   * constant in this codebase. The screen compares against a delivered
   * figure; the verdict still comes back from the endpoint.
   *
   * On **every** answer, not just the first: a catalogue fetched once and
   * kept in a store is a catalogue that goes stale without anyone noticing.
   * Optional here because the fixtures of the older suites predate it.
   */
  gratingSpecs?: GratingSpecOut[];

  /**
   * The narrowest interruption the model has to be able to express — the
   * width of an `RR 120` (design §5.3).
   *
   * The board caps its snap radius at half of it. Uncapped, 12 px are 440 mm
   * of real wall on a 17-metre facade: the pointer would snap across a real
   * downpipe, two runs would read as one, and the bracket count would come
   * out one short — the direction that is only noticed on site.
   */
  minGapMm?: number;

  /**
   * The bracket part of the parts list — one row per article, or per open
   * question where none was found. Planning path only, empty or absent
   * elsewhere for the same reason `gratingSpecs` is (backend
   * `DemoRunOut.console_lines`).
   */
  consoleLines?: DemoConsoleLine[];

  /**
   * Every bracket the plan counted, addressed — planning path only, empty or
   * absent elsewhere for the same reason `consoleLines` is (backend
   * `DemoRunOut.console_marks`). What the board draws its right-click chooser
   * against, and what tells a mark it has drifted from its own proposal.
   */
  consoleMarks?: DemoConsoleMark[];

  /**
   * Every active article the bracket chooser may offer — planning path only,
   * empty or absent elsewhere for the same reason `consoleMarks` is (backend
   * `DemoRunOut.console_catalogue`). What the chooser lists beside the
   * proposal, not only what `consoleLines`/`consoleMarks` already mention.
   */
  consoleCatalogue?: DemoConsoleCatalogueEntry[];

  /**
   * Per belegte wall, what each splitting rule would lay there and what it
   * comes to — PRD §22.5 Fall C (backend `DemoRunOut.split_variants`, design
   * 2026-10-14). Planning path only. **Describes, never changes**: the parts
   * list and the brackets are the grating that lies.
   */
  splitVariants?: DemoWallSplitVariants[];

  /** The splitting-rule catalogue, in the order every list shows it —
   * planning path only, for the reason `gratingSpecs` is. */
  splitRules?: DemoSplitRule[];

  /** Measured milliseconds per step, plus `total`. */
  timings: Partial<Record<DemoStep | "total", number>>;

  // ── Provenance of a dropped drawing (`POST /demo/upload`) ───────────────
  //
  // The backend models these as `DemoUploadOut(DemoRunOut)` — the ordinary
  // run, with where its answer came from written on it. They are declared
  // here rather than on a second result type so the three screens keep
  // reading one shape; what fills them is the upload path. A result fetched
  // through `/demo/run` — a re-run after a correction, say — has no drop to
  // report and carries none of them, so read them off the run that came out
  // of the drop, never off a later one.

  /**
   * Which registered case a dropped drawing was recognised as, or `null`.
   *
   * `null` is not a failure: it says the drawing is not one of ours, which is
   * the normal case for a customer's file and the reason the live path exists
   * (design 12.08.2026 §2).
   */
  matchedCase: string | null;

  /**
   * Where this answer came from — a record on file, or the model, just now.
   *
   * It travels because the two are indistinguishable on screen, and that is
   * exactly why the difference has to be sayable out loud: a recorded run is
   * Provenienz, not an Attrappe (design §2.3).
   */
  source: "recording" | "live";

  /**
   * The file names as they were dropped, in that order — not
   * `document.filename`, which is what the chain made of them. A person
   * looking at the screen recognises the thing they dragged in.
   */
  uploadedNames: string[];
}

/** How the run reached this process. `manual` is the one that reached no model at all. */
export type DemoProvider = "mistral" | "replay" | "manual";

/**
 * One wall dragged perpendicular to itself (spec §6.1), mirroring the
 * backend's own `EdgeMoveIn`. `segmentIndex` is a position in the chosen
 * arrangement's own segment list, never an `edgeId` — two runs may answer to
 * the same id and then build the same edge ids too, so an id alone cannot
 * name one wall.
 */
export interface EdgeMoveInput {
  segmentIndex: number;
  deltaMm: number;
}

export interface DemoRunInput {
  caseId: string;
  provider: DemoProvider;
  /**
   * Grating type from the enquiry (F-02/F-03), not read off the drawing.
   * The backend turns it into the build width and says which source it used.
   */
  gratingType?: string;
  /** Which arrangement to draw. Leaving it out keeps the runs separate. */
  outline?: string;
  /**
   * Every correction made to the chosen arrangement, in the order they were
   * made. Complete on every request — this process holds no session (spec
   * §6.2), so leaving it out means "as read", the same as sending `[]`.
   */
  edits?: EdgeMoveInput[];
}

/**
 * One wall a correction touched, before and after — the backend's own
 * `AdjustmentOut`. Reported per wall, not per drag: a wall dragged twice
 * appears once, with the length it started at and the length it ended at.
 */
export interface DemoAdjustment {
  segmentIndex: number;
  edgeId: string;
  runId: string;
  deltaMm: number;
  lengthBeforeMm: number;
  lengthAfterMm: number;
}

/**
 * A named state, never a bare string.
 *
 * Every way the run can end carries an `errorClass` from the backend's own
 * vocabulary, so the screen can say what happened instead of showing a blank
 * page (spec §5). A failure to reach the process at all gets one too.
 */
export class DemoError extends Error {
  readonly errorClass: string;
  readonly status: number;

  constructor(errorClass: string, message: string, status = 0) {
    super(message);
    this.name = "DemoError";
    this.errorClass = errorClass;
    this.status = status;
  }
}

/** The page as it was sent to the model — turned, if step ① turned it. */
export function demoPageUrl(documentId: string, page: number): string {
  return `${DEMO_BASE_URL}/demo/pages/${encodeURIComponent(documentId)}/${page}`;
}

/**
 * One request to the demo process, with every way it can fail already named.
 *
 * Both entrances share it — a drawing chosen by id and a drawing dropped on
 * the screen end in the same run, and a failure has to read the same either
 * way (`DemoError`, spec §5). The two differ only in what they send.
 */
async function send<T = DemoRunResult>(path: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${DEMO_BASE_URL}${path}`, init);
  } catch (cause) {
    throw new DemoError(
      "demo_unreachable",
      `${DEMO_BASE_URL} antwortet nicht: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  if (!response.ok) {
    // The backend answers a failure as {errorClass, message}. Anything else
    // reaching this point is a process that is not the demo entrypoint.
    const payload: unknown = await response.json().catch(() => null);
    const named =
      payload && typeof payload === "object"
        ? (payload as { errorClass?: unknown; message?: unknown })
        : {};
    throw new DemoError(
      typeof named.errorClass === "string" ? named.errorClass : "unknown_error",
      typeof named.message === "string" ? named.message : `HTTP ${response.status}`,
      response.status,
    );
  }

  return (await response.json()) as T;
}

export async function runDemo(input: DemoRunInput): Promise<DemoRunResult> {
  return send("/demo/run", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      case: input.caseId,
      provider: input.provider,
      gratingType: input.gratingType ?? null,
      outline: input.outline ?? null,
      // `[]`, not `null`: the backend's own default is `edits: list[EdgeMoveIn] = []`
      // (`RunRequest.edits`), and an edit list is never "absent" the way a
      // grating type or an outline choice can be — it is either what
      // somebody corrected, or nothing yet.
      edits: input.edits ?? [],
    }),
  });
}

/**
 * A drawing that was dropped, not chosen (design 12.08.2026 §1).
 *
 * The files go up as multipart under the field name `files`, which is what
 * `POST /demo/upload` reads them from. **No `content-type` header is set
 * here**: the boundary is part of that header and only the browser knows it,
 * so writing the header by hand produces a body the backend cannot take
 * apart.
 *
 * No `provider` travels. Which one answers is the backend's decision and
 * follows from the drawing itself — a recognised one is replayed, an unknown
 * one goes live (design §2) — and that is reported back in `source`, not
 * asked for here.
 */
export async function uploadDemo(
  files: File[],
  opts: { gratingType?: string; caseId?: string } = {},
): Promise<DemoRunResult> {
  const body = new FormData();
  for (const file of files) body.append("files", file);
  // Only when it was stated. An empty field would arrive as the string "" and
  // read as a grating type nobody named.
  if (opts.gratingType) body.append("gratingType", opts.gratingType);
  // Task 12's own form field: given, the sheets are written onto this Vorgang
  // (real rows, real bytes in the Ablage) instead of a temporary directory,
  // and the endpoint then requires a session — omitted, this call is
  // unchanged from before Task 12 (`demo.py`'s own `upload` docstring).
  if (opts.caseId) body.append("caseId", opts.caseId);

  return send("/demo/upload", { method: "POST", body });
}

/** One wall of an outline somebody drew, on the wire. */
export interface ManualWallInput {
  /** What was typed for this wall, in millimetres. `null` while nothing was. */
  lengthMm: number | null;
  label: string | null;
}

/**
 * A corner cut off by a slant (Fase, design 2026-10-18 §4), as the hand path
 * sends it: `wallIndex` is the index in `walls` of the wall whose **end**
 * corner is cut, the legs are whole millimetres from the imagined corner
 * point — `legAMm` back along that wall, `legBMm` on along the next one.
 */
export interface ManualChamferInput {
  wallIndex: number;
  legAMm: number;
  legBMm: number;
}

/** An outline somebody drew, whole — this protocol holds no session. */
export interface ManualOutlineInput {
  vertices: { xMm: number; yMm: number }[];
  closed: boolean;
  /** Always `vertices.length - 1` long, in the order the walk was drawn. */
  walls: ManualWallInput[];
  /**
   * The cut corners. Left out, there are none — and left out on purpose by
   * „Kalibrieren“, whose answer has to name exactly the draft's corners: the
   * answer's outline shows every chamfer as a wall of its own.
   */
  chamfers?: ManualChamferInput[];
}

/**
 * An outline made by hand rather than read (design 2026-08-15 §3.3).
 *
 * No drawing was uploaded and no model was asked, so nothing about a document,
 * a provider or a recording travels — the shape and the typed figures are the
 * whole request. What comes back is the ordinary `DemoRunResult`, because
 * everything behind this point is the same: the same closure, the same
 * findings, the same drawing (§2).
 *
 * The document goes over complete on every call. There is no session and no
 * saved draft; a reload starts from nothing, and the screen says so.
 */
export async function manualOutline(input: ManualOutlineInput): Promise<DemoRunResult> {
  return send("/demo/outline/manual", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

/** One wall of a settled outline, as the planning screen hands it back. */
export interface PlanWallInput {
  lengthMm: number | null;
  /**
   * Where that length came from. It travels because §3.4 stands on it: only
   * `read` and `manual` state a length, and only a wall that states one may
   * carry grating. Without it the endpoint would have to believe every figure
   * it is given — exactly the check that may not hang on the client.
   */
  lengthSource: LengthSource;
  label: string | null;
  /**
   * `"fase"` on a chamfer's slant (backend `PlanWallIn.kind`, design
   * 2026-10-18). The planner takes a wall that runs along neither axis only
   * where it is marked so; unmarked, the whole board is a 422 („läuft
   * schräg“). Optional: the backend defaults to `"wall"`.
   */
  kind?: "wall" | "fase";
  /** On a wall a chamfer shortened, the customer's measure up to the corner
   * point — handed back as the answer said it; planning counts `lengthMm`. */
  cornerPointLengthMm?: number | null;
}

/**
 * The settled outline the planning screen works on.
 *
 * Deliberately not `ManualOutlineInput`: that one is a *draft* — what somebody
 * typed, before the solver ran. Both ways into planning (a drawing that was
 * read, a shape that was drawn) hold the same thing after the schema step,
 * and this is it.
 */
export interface PlanOutlineInput {
  vertices: { xMm: number; yMm: number }[];
  closed: boolean;
  walls: PlanWallInput[];
  /** The corners somebody treated, by the wall whose **end** each one is.
   * Left out where none is, the same statement as an empty list. */
  corners?: CornerInput[];
  /** The doors, sills and pipes standing on a wall. Left out where none is,
   * the same statement as an empty list — the same treatment `corners` gets. */
  features?: FeatureInput[];
  /** The holes in the grating, the same treatment again. */
  cutouts?: CutoutInput[];
}

/** One treated corner on the wire — `CornerMark` from `lib/placement`, as sent. */
export interface CornerInput {
  wallIndex: number;
  treatment: "gehrung" | "fuellung";
  /** Only on `fuellung`: whose grating covers the corner square whole. */
  filledWallIndex: number | null;
  kindOverride: "aussen" | "innen" | null;
  gainOverrideMm: number | null;
}

/**
 * One facade object on the wire — `FacadeFeature` from `lib/placement`, as
 * sent. Matches `FeatureIn` in `apps/api/app/demo.py` field for field; this
 * entrypoint is not in the OpenAPI contract (`demo.py`'s app is not mounted
 * there), so the mapping is written by hand, beside `CornerInput` above.
 */
export interface FeatureInput {
  id: string | null;
  wallIndex: number;
  offsetMm: number;
  lengthMm: number;
  featureType: string;
  covered: boolean;
  widthOffsetMm: number;
  splitsRun: boolean;
  label: string | null;
}

export interface CutoutReferenceInput {
  kind: ReferenceKind;
  featureId: string | null;
  side: ReferenceSide | null;
}

/** One hole on the wire — `Cutout` from `lib/placement`, as sent. Matches
 * `CutoutIn` in `apps/api/app/demo.py` field for field. */
export interface CutoutInput {
  id: string;
  wallIndex: number;
  offsetMm: number;
  acrossMm: number;
  shape: CutoutShape;
  diameterMm: number | null;
  sizeAlongMm: number | null;
  sizeAcrossMm: number | null;
  obstacleType: string;
  confirmed: boolean;
  confirmedOn: string | null;
  featureId: string | null;
  reference: CutoutReferenceInput;
}

export interface PlanInput {
  outline: PlanOutlineInput;
  /** The type of the job. `null` where nobody has stated one yet. */
  gratingType: string | null;
  placements: PlacementInput[];
  /**
   * Which fixed bracket family to try first — a preference, not a filter
   * (backend `PlanRequest.console_series`). Solid 2.0 is fastened from the
   * side, Solid 1.0 from above; the catalogue may carry only one of the two
   * for a given width, and then the other is used regardless. Optional here
   * because the backend's own default (`"solid_1"`) covers a caller that
   * sends none — this screen always does.
   */
  consoleSeries?: "solid_1" | "solid_2";
  /**
   * Whether Vario is proposed before a fixed bracket. Default is Solid
   * first (Entscheid Denis, 07.09.2026); Vario is the per-case choice.
   */
  consoleAdjustableFirst?: boolean;
  /**
   * Bracket address → article number, for the ones a person set by hand —
   * `DemoConsoleMark.key` verbatim, never an index (backend
   * `PlanRequest.console_overrides`).
   */
  consoleOverrides?: Record<string, string>;
  /** The surcharge formula's two parameters (D-6), as decimal strings —
   * money never travels as a float. `null` is „not named yet". */
  cutoutRatePerMNet?: string | null;
  cutoutBaseNet?: string | null;
  /** Hole id → what somebody typed for its surcharge, and what that displaced. */
  cutoutSurcharges?: Record<string, { typed: string | null; displaced: string | null }>;
  /**
   * Which splitting rule „Wand belegen" lays outside a width zone, per
   * Vorgang (backend `PlanRequest.split_priority`, PRD §22.5 Fall C). A
   * prefill: on `/plan/fill` it decides what is laid, on every other answer
   * only which variant is `preferred`. Optional because the backend's default
   * (`"standard_first"`) covers a caller that sends none — this screen
   * always does.
   */
  splitPriority?: SplitPriority;
}

/** The three splitting rules a Vorgang may prefer — backend
 * `planning.SplitPriority`, verbatim. */
export type SplitPriority = "standard_first" | "fewest_elements" | "even";

/** One entry of `DemoRunResult.splitRules` (backend `SplitRuleOut`). */
export interface DemoSplitRule {
  priority: SplitPriority;
  /** „Standardlänge zuerst" — German, from the backend. */
  label: string;
  description: string;
}

/** One element of a variant, with its resolved width. */
export interface DemoSplitVariantElement {
  offsetMm: number;
  lengthMm: number;
  widthMm: number;
}

/** What „Wand neu aufteilen" would lay on one wall under `rules` (backend
 * `SplitVariantOut`). `consoles` is the whole plan's figure with this
 * variant laid; `consolesDelta` the difference to `totals.consoles`. */
export interface DemoSplitVariant {
  rules: SplitPriority[];
  label: string;
  elements: DemoSplitVariantElement[];
  elementCount: number;
  standardCount: number;
  specialLengthsMm: number[];
  consoles: number;
  consolesDelta: number;
  middleConsoles: number;
  blanksOverMaximum: number;
  consolesDerivation: string;
  preferred: boolean;
  current: boolean;
  /** The evaluation in one German sentence. */
  summary: string;
}

/** The variants of one belegte wall, in the rules' order — never re-sorted. */
export interface DemoWallSplitVariants {
  wallIndex: number;
  lengthMm: number;
  variants: DemoSplitVariant[];
}

/** One placement on the wire — `Placement` from `lib/placement`, as sent. */
export interface PlacementInput {
  id: string;
  kind: "grating" | "gap" | "bracket" | "area";
  wallIndex: number;
  offsetMm: number;
  lengthMm: number;
  gratingType: string | null;
  widthMm: number | null;
  /** Where that special width lies: `true` grows it away from the wall.
   * Always sent — `Placement` may lack it, the wire never does. */
  widensOutward: boolean;
  splitsRun: boolean;
  label: string | null;
  depthMm: number | null;
  /** A blank somebody typed instead of the derived one, `null` otherwise. */
  blankLengthMm?: number | null;
  /** What the derivation said when that blank was typed. Carried, never
   * calculated with. */
  blankSnapshotMm?: number | null;
}

/**
 * Everything about a stored plan that is **not** geometry — the backend's
 * `PlanningStateIn`, as a version carries it and as `GET /cases/{id}/plan`
 * hands it back (`DemoRunResult.planningState`).
 *
 * The same fields this screen posts on every version, in the same
 * camelCase: the round trip is byte-for-byte, and a key renamed in transit
 * is a loss the next replay cannot undo.
 */
export interface DemoPlanningState {
  placements: PlacementInput[];
  consoleSeries: "solid_1" | "solid_2";
  consoleAdjustableFirst: boolean;
  consoleOverrides: Record<string, string>;
  cutoutClearanceMm: number;
  cutoutRatePerMNet: string | null;
  cutoutBaseNet: string | null;
  cutoutSurcharges: Record<string, { typed: string | null; displaced: string | null }>;
  /** Absent only on fixtures older than 14.10.2026; the server always sends it. */
  splitPriority?: SplitPriority;
}

/**
 * A plan, whole, answered with its runs, its consoles and its findings.
 *
 * Stateless like every other call on this path: the client holds the document
 * and sends all of it. What comes back is the ordinary `DemoRunResult`, plus
 * the grating catalogue and `minGapMm` — both of which the board needs and
 * neither of which it may know by itself (design §3.2, §5.3).
 *
 * **A wrong plan is answered, not refused** (design §3.5.1). An element that
 * visibly runs past the end of its wall is the clearest error message this
 * system can produce, and it only exists if the wrong plan is drawn. Only the
 * undrawable — an unknown wall, a wall with no stated length — comes back as
 * a failure.
 */
export async function planManual(input: PlanInput): Promise<DemoRunResult> {
  return send("/demo/plan/manual", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

/**
 * Cover one wall in one go, by the rule that governs every other split.
 *
 * Answers with the placements it produced **and** their plan: the button has
 * to show a result, and two round trips for one press is a wait nobody asked
 * for. It is the only call that invents placements, and it says so by handing
 * them back rather than keeping them.
 */
export async function fillWall(
  /** One wall by index, or several by list — exactly one of the two ways.
   * The endpoint refuses both-or-neither; a list skips walls without a
   * figure and names them in `skippedWalls`, where the single wall refuses
   * (somebody pointed at it). */
  input: PlanInput & {
    fillWallIndex?: number;
    fillWallIndices?: number[];
    /** Re-lay the named walls instead of topping them up — „Wand neu
     * aufteilen". Off by default, which is „Wand belegen": what is free gets
     * covered and what is already laid stays. */
    replaceExisting?: boolean;
  },
): Promise<{
  placements: PlacementInput[];
  result: DemoRunResult;
  skippedWalls?: number[];
  /** How many elements the re-laying took off, and how many of those carried
   * a figure somebody typed by hand. Both `0` unless `replaceExisting`. */
  replaced?: number;
  replacedHandTyped?: number;
}> {
  const response = await fetch(`${DEMO_BASE_URL}/demo/plan/fill`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  }).catch((cause: unknown) => {
    throw new DemoError(
      "demo_unreachable",
      `${DEMO_BASE_URL} antwortet nicht: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  });

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const named =
      payload && typeof payload === "object"
        ? (payload as { errorClass?: unknown; message?: unknown })
        : {};
    throw new DemoError(
      typeof named.errorClass === "string" ? named.errorClass : "unknown_error",
      typeof named.message === "string" ? named.message : `HTTP ${response.status}`,
      response.status,
    );
  }

  return (await response.json()) as {
    placements: PlacementInput[];
    result: DemoRunResult;
    skippedWalls?: number[];
    replaced?: number;
    replacedHandTyped?: number;
  };
}


/**
 * „Rohling aufteilen" — divide one element whose blank ran past 1.500 mm.
 *
 * The finding `miter_blank_over_maximum` stays a finding; this is the way out
 * offered under it (Entwurf 2026-08-29 §13), the same shape „So aufteilen"
 * has under a stretch that is too short. Answers with the whole document and
 * its plan, like `fillWall`.
 *
 * **A division that does not hold is not an error.** The answer then carries
 * the document unchanged, `pieces: 0` and a German `refusal` the screen shows
 * verbatim — nothing is wrong with the plan, the offer simply does not apply.
 */
export async function splitBlank(
  input: PlanInput & { placementId: string },
): Promise<{
  placements: PlacementInput[];
  result: DemoRunResult;
  /** How many elements the division made of the one — `0` where none was made. */
  pieces: number;
  /** Why nothing was divided, in German, or `null` where something was. */
  refusal: string | null;
}> {
  return send<{
    placements: PlacementInput[];
    result: DemoRunResult;
    pieces: number;
    refusal: string | null;
  }>("/demo/plan/split-blank", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}
